import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, Trash2, Upload, Droplets, BedDouble, Bath, X, Ruler, Sparkles, LayoutGrid, Rows3, Pencil, Camera, Share2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PropertyOffers from "@/components/PropertyOffers";
import PropertyViewings from "@/components/PropertyViewings";
import PropertyRooms from "@/components/PropertyRooms";
import PropertyParcels from "@/components/PropertyParcels";
import PropertyDossier from "@/components/PropertyDossier";
import Timeline from "@/components/Timeline";
import DocList from "@/components/DocList";
import EmailShareDialog from "@/components/EmailShareDialog";
import { shareOrDownloadFile } from "@/lib/share";
import { api, API, fmtEuro, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSearchableList } from "@/hooks/useSearchableList";
import { PageHeader, Field, EmptyState, CopyButton, inputCls, RefBadge, SortableTh } from "@/components/shared";

const PLACEHOLDER = "https://images.pexels.com/photos/34688219/pexels-photo-34688219.jpeg?auto=compress&w=800";

const emptyProperty = {
  name: "", address_private: "", location_public: "",
  postcode: "", city: "", estimated_value: "",
  price_net: "", agency_fees: "", price_total: "",
  property_type: "", bedrooms: "", bathrooms: "", habitable_space: "", land_size: "",
  outbuildings: "", pool: false, gite_potential: false, heating: "", drainage: "",
  photos: [], floorplans: [], documents: [], advert_en: "", advert_fr: "",
  summary: "", description: "", selling_points: ["", "", "", "", ""], notes: "",
};

const num = (v) => (v === "" || v == null ? null : Number(v));

const shareStoredFile = async (path, name, t) => {
  try {
    const result = await shareOrDownloadFile(path, name);
    if (result === "downloaded") toast.info(t("fileDownloaded"));
  } catch {
    toast.error("Share failed");
  }
};

const PhotoList = ({ field, testPrefix, form, setForm, uploadFile, uploading, addLabel, cameraLabel, t }) => (
  <div className="flex flex-wrap gap-2">
    {(form[field] || []).map((p, i) => (
      <div key={p} className="relative group">
        <img src={`${API}/files/${p}`} alt="" className="h-20 w-28 object-cover rounded-lg border border-stone-200" />
        <button
          data-testid={`${testPrefix}-share-${i}`}
          onClick={() => shareStoredFile(p, `${field}-${i + 1}.${p.split(".").pop()}`, t)}
          title={t("share")}
          className="absolute -top-1.5 -left-1.5 bg-slate-900 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Share2 size={12} />
        </button>
        <button
          data-testid={`${testPrefix}-remove-${i}`}
          onClick={() => setForm({ ...form, [field]: form[field].filter((x) => x !== p) })}
          className="absolute -top-1.5 -right-1.5 bg-slate-900 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <X size={12} />
        </button>
      </div>
    ))}
    <label className={`h-20 w-28 flex flex-col items-center justify-center gap-1 border border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-stone-400 text-stone-400 hover:text-stone-600 transition-colors text-[11px] ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
      <Upload size={16} strokeWidth={1.5} />
      {uploading ? "…" : addLabel}
      <input data-testid={`${testPrefix}-upload-input`} type="file" accept="image/*,.pdf" className="hidden"
        onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], field)} />
    </label>
    {/* Direct-to-camera on iPhone/iPad (capture is ignored on desktop) */}
    <label className={`h-20 w-28 flex flex-col items-center justify-center gap-1 border border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-stone-400 text-stone-400 hover:text-stone-600 transition-colors text-[11px] ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
      <Camera size={16} strokeWidth={1.5} />
      {uploading ? "…" : cameraLabel}
      <input data-testid={`${testPrefix}-camera-input`} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], field)} />
    </label>
  </div>
);

export default function Properties() {
  const { items, search, setSearch, reload } = useSearchableList("/properties");
  const [form, setForm] = useState(null);

  // Memoized so the bullet list isn't rebuilt on every keystroke in the dialog
  const sellingPointsCopyText = useMemo(
    () => (form?.selling_points || []).filter((s) => s && s.trim()).map((s) => `• ${s.trim()}`).join("\n"),
    [form?.selling_points]
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("properties.view") || "cards");
  const [initialTab, setInitialTab] = useState("details");
  const [emailShareOpen, setEmailShareOpen] = useState(false);
  const [sortKey, setSortKey] = useState("ref_code");
  const [sortDir, setSortDir] = useState("asc");
  useEffect(() => { localStorage.setItem("properties.view", viewMode); }, [viewMode]);
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };
  const [generatingAdvert, setGeneratingAdvert] = useState(false);
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open a property when navigated from the Pipeline via /properties?open=<id>
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || !items.length) return;
    const found = items.find((p) => p.id === openId);
    if (found) {
      setInitialTab(searchParams.get("tab") || "details");
      setForm({
        ...emptyProperty, ...found,
        selling_points: [...(found.selling_points || []), "", "", "", "", ""].slice(0, 5),
      });
      // Clear the query params so re-navigating works cleanly
      searchParams.delete("open");
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    }
  }, [items, searchParams, setSearchParams]);

  const generateAdvert = async () => {
    if (!form.id) return toast.error(t("saveFirst"));
    setGeneratingAdvert(true);
    try {
      const { data } = await api.post(`/ai/property-advert/${form.id}`, {
        summary: form.summary,
        description: form.description,
        selling_points: form.selling_points.filter((s) => s && s.trim()),
      }, { timeout: 120000 });
      setForm((f) => ({ ...f, advert_en: data.advert_en, advert_fr: data.advert_fr }));
      toast.success(t("advertsGenerated"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setGeneratingAdvert(false);
    }
  };

  const openProperty = (p) => {
    setInitialTab("details");
    setForm({
      ...emptyProperty, ...p,
      selling_points: [...(p.selling_points || []), "", "", "", "", ""].slice(0, 5),
    });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("required"));
    setSaving(true);
    const payload = {
      ...form,
      selling_points: form.selling_points.filter((s) => s && s.trim()),
      price_net: num(form.price_net), agency_fees: num(form.agency_fees), price_total: num(form.price_total),
      estimated_value: num(form.estimated_value),
      bedrooms: num(form.bedrooms), bathrooms: num(form.bathrooms),
      habitable_space: num(form.habitable_space), land_size: num(form.land_size),
    };
    try {
      if (form.id) await api.put(`/properties/${form.id}`, payload);
      else await api.post("/properties", payload);
      toast.success(t("saved"));
      setForm(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/properties/${form.id}`);
      toast.success(t("deleted"));
      setForm(null);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const uploadFile = async (file, field) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: [...(f[field] || []), data.path] }));
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="properties-page">
      <PageHeader
        title={t("properties")}
        action={
          <button data-testid="add-property-button" onClick={() => setForm({ ...emptyProperty })}
            className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors">
            <Plus size={16} strokeWidth={1.5} /> {t("addProperty")}
          </button>
        }
      />
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input data-testid="properties-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t("search")} className={`${inputCls} pl-9`} />
        </div>
        <div className="inline-flex bg-stone-100 border border-stone-200 rounded-lg p-0.5" role="tablist">
          <button
            data-testid="properties-view-cards"
            onClick={() => setViewMode("cards")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === "cards" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-900"}`}
          >
            <LayoutGrid size={13} strokeWidth={1.75} /> {t("viewCards")}
          </button>
          <button
            data-testid="properties-view-table"
            onClick={() => setViewMode("table")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === "table" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-900"}`}
          >
            <Rows3 size={13} strokeWidth={1.75} /> {t("viewTable")}
          </button>
        </div>
      </div>

      {items.length === 0 ? <EmptyState message={t("noResults")} /> : viewMode === "table" ? (
        <PropertiesTable items={items} sortKey={sortKey} sortDir={sortDir} toggleSort={toggleSort} onOpen={openProperty} t={t} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((p) => (
            <div key={p.id} className="relative bg-white border border-stone-200 rounded-xl overflow-hidden hover:border-stone-300 hover:shadow-sm transition-all duration-200">
              <button data-testid={`property-card-${p.id}`} onClick={() => openProperty(p)} className="text-left w-full">
                <img
                  src={p.photos?.length ? `${API}/files/${p.photos[0]}` : PLACEHOLDER}
                  alt={p.name}
                  className="h-40 w-full object-cover"
                  onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                />
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <RefBadge code={p.ref_code} />
                  </div>
                  <h3 className="font-heading text-lg font-medium text-stone-900">{p.name}</h3>
                  <p className="text-xs text-stone-400 mt-0.5 line-clamp-1">{p.location_public}</p>
                  <p className="text-base font-medium text-stone-900 mt-2">{fmtEuro(p.price_total)} <span className="text-xs text-stone-400 font-normal">FAI</span></p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-stone-500">
                    {p.bedrooms != null && <span className="flex items-center gap-1"><BedDouble size={14} strokeWidth={1.5} />{p.bedrooms}</span>}
                    {p.bathrooms != null && <span className="flex items-center gap-1"><Bath size={14} strokeWidth={1.5} />{p.bathrooms}</span>}
                    {p.habitable_space != null && <span>{p.habitable_space} m²</span>}
                    {p.pool && <span className="flex items-center gap-1 text-[#2F4F4F]"><Droplets size={14} strokeWidth={1.5} />{t("pool")}</span>}
                  </div>
                </div>
              </button>
              <button data-testid={`property-rooms-${p.id}`} onClick={() => navigate(`/properties/${p.id}/rooms`)}
                className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm border border-stone-200 hover:bg-white text-stone-700 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors">
                <Ruler size={13} strokeWidth={1.5} /> {t("rooms")}
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-3xl bg-white">
          {form && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl font-medium text-stone-900 flex items-center gap-3 flex-wrap">
                  <span>{form.id ? t("editProperty") : t("addProperty")}</span>
                  <RefBadge code={form.ref_code} size="md" />
                </DialogTitle>
              </DialogHeader>
              <Tabs defaultValue={initialTab} className="mt-2">
                <TabsList className="bg-stone-100 h-auto p-1">
                  <TabsTrigger value="details" data-testid="property-tab-details">Info</TabsTrigger>
                  <TabsTrigger value="media" data-testid="property-tab-media">{t("photos")}</TabsTrigger>
                  <TabsTrigger value="adverts" data-testid="property-tab-adverts">Adverts</TabsTrigger>
                  {form.id && <TabsTrigger value="offers" data-testid="property-tab-offers">{t("offersTab")}</TabsTrigger>}
                  {form.id && <TabsTrigger value="viewings" data-testid="property-tab-viewings">{t("viewings")}</TabsTrigger>}
                  {form.id && <TabsTrigger value="rooms" data-testid="property-tab-rooms">{t("roomsTab")}</TabsTrigger>}
                  {form.id && <TabsTrigger value="parcels" data-testid="property-tab-parcels">{t("parcelsTab")}</TabsTrigger>}
                  {form.id && <TabsTrigger value="dossier" data-testid="property-tab-dossier">{t("dossierTab")}</TabsTrigger>}
                  {form.id && <TabsTrigger value="timeline" data-testid="property-tab-timeline">{t("timelineTab")}</TabsTrigger>}
                </TabsList>
                <TabsContent value="details">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <Field label={t("name")} className="sm:col-span-2"><input data-testid="property-name-input" className={inputCls} value={form.name} onChange={set("name")} /></Field>
                    <Field label={t("locationPublic")} className="sm:col-span-2"><input data-testid="property-location-input" className={inputCls} value={form.location_public} onChange={set("location_public")} /></Field>
                    <Field label={t("city")}><input data-testid="property-city-input" className={inputCls} value={form.city || ""} onChange={set("city")} /></Field>
                    <Field label={t("postcode")}><input data-testid="property-postcode-input" className={inputCls} value={form.postcode || ""} onChange={set("postcode")} /></Field>
                    <Field label={t("addressPrivate")} className="sm:col-span-2"><textarea rows={2} data-testid="property-address-input" className={inputCls} value={form.address_private} onChange={set("address_private")} /></Field>
                    <Field label={t("priceNet")}><input data-testid="property-price-net-input" type="number" className={inputCls} value={form.price_net ?? ""} onChange={set("price_net")} /></Field>
                    <Field label={t("agencyFees")}><input data-testid="property-fees-input" type="number" className={inputCls} value={form.agency_fees ?? ""} onChange={set("agency_fees")} /></Field>
                    <Field label={t("priceTotal")}><input data-testid="property-price-total-input" type="number" className={inputCls} value={form.price_total ?? ""} onChange={set("price_total")} /></Field>
                    <Field label={t("estimatedValue")}><input data-testid="property-estimated-input" type="number" className={inputCls} value={form.estimated_value ?? ""} onChange={set("estimated_value")} /></Field>
                    <Field label={t("propertyType")}><input data-testid="property-type-input" className={inputCls} value={form.property_type} onChange={set("property_type")} /></Field>
                    <Field label={t("bedrooms")}><input type="number" data-testid="property-bedrooms-input" className={inputCls} value={form.bedrooms ?? ""} onChange={set("bedrooms")} /></Field>
                    <Field label={t("bathrooms")}><input type="number" data-testid="property-bathrooms-input" className={inputCls} value={form.bathrooms ?? ""} onChange={set("bathrooms")} /></Field>
                    <Field label={t("habitableSpace")}><input type="number" data-testid="property-space-input" className={inputCls} value={form.habitable_space ?? ""} onChange={set("habitable_space")} /></Field>
                    <Field label={t("landSize")}><input type="number" data-testid="property-land-input" className={inputCls} value={form.land_size ?? ""} onChange={set("land_size")} /></Field>
                    <Field label={t("outbuildings")}><input className={inputCls} value={form.outbuildings} onChange={set("outbuildings")} /></Field>
                    <Field label={t("heating")}><input className={inputCls} value={form.heating} onChange={set("heating")} /></Field>
                    <Field label={t("drainage")}><input className={inputCls} value={form.drainage} onChange={set("drainage")} /></Field>
                    <div className="flex items-center gap-8 sm:col-span-2 bg-stone-50 border border-stone-200 rounded-lg p-4">
                      <label className="flex items-center gap-2.5 text-sm text-stone-700 cursor-pointer">
                        <Checkbox data-testid="property-pool-checkbox" checked={form.pool} onCheckedChange={(c) => setForm({ ...form, pool: !!c })} /> {t("pool")}
                      </label>
                      <label className="flex items-center gap-2.5 text-sm text-stone-700 cursor-pointer">
                        <Checkbox data-testid="property-gite-checkbox" checked={form.gite_potential} onCheckedChange={(c) => setForm({ ...form, gite_potential: !!c })} /> {t("gitePotential")}
                      </label>
                    </div>
                    <Field label={t("notes")} className="sm:col-span-2"><textarea data-testid="property-notes-input" rows={3} className={inputCls} value={form.notes} onChange={set("notes")} /></Field>
                  </div>
                </TabsContent>
                <TabsContent value="media">
                  <div className="space-y-6 mt-4">
                    <button
                      data-testid="property-share-email-button"
                      onClick={() => setEmailShareOpen(true)}
                      className="inline-flex items-center gap-2 border border-stone-300 hover:border-stone-400 bg-white text-stone-700 hover:text-stone-900 rounded-lg px-3 py-2 text-xs font-medium transition-colors">
                      <Mail size={14} strokeWidth={1.5} /> {t("emailFiles")}
                    </button>
                    <Field label={t("photos")}><PhotoList field="photos" testPrefix="property-photo" form={form} setForm={setForm} uploadFile={uploadFile} uploading={uploading} addLabel={t("add")} cameraLabel={t("takePhoto")} t={t} /></Field>
                    <Field label={t("floorplans")}><PhotoList field="floorplans" testPrefix="property-floorplan" form={form} setForm={setForm} uploadFile={uploadFile} uploading={uploading} addLabel={t("add")} cameraLabel={t("takePhoto")} t={t} /></Field>
                    <Field label={t("documents")}>
                      <p className="text-[11px] text-stone-400 mb-2">{t("documentsHint")}</p>
                      <DocList
                        items={form.documents}
                        onChange={(next) => setForm({ ...form, documents: next })}
                        testPrefix="property-document"
                        addLabel={t("addDocument")}
                        cameraLabel={t("takePhoto")}
                        uploading={uploading}
                        setUploading={setUploading}
                      />
                    </Field>
                  </div>
                </TabsContent>
                <TabsContent value="adverts">
                  <div className="space-y-5 mt-4">
                    {form.id && (
                      <button
                        data-testid="generate-advert-button"
                        onClick={generateAdvert}
                        disabled={generatingAdvert}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#2F4F4F] hover:bg-[#243c3c] text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60">
                        <Sparkles size={15} strokeWidth={1.5} />
                        {generatingAdvert ? t("generating") : t("generateAdvert")}
                      </button>
                    )}
                    <Field label={t("summary")}>
                      <textarea data-testid="property-summary-input" rows={2} className={inputCls} value={form.summary} onChange={set("summary")} />
                      <div className="mt-1"><CopyButton text={form.summary} testId="copy-summary-button" /></div>
                    </Field>
                    <Field label={t("descriptionLabel")}>
                      <textarea data-testid="property-description-input" rows={5} className={inputCls} value={form.description} onChange={set("description")} />
                      <div className="mt-1"><CopyButton text={form.description} testId="copy-description-button" /></div>
                    </Field>
                    <Field label={t("sellingPoints")}>
                      <div className="space-y-2">
                        {form.selling_points.map((sp, i) => (
                          <input
                            key={`sp-slot-${i}`}
                            data-testid={`property-selling-point-${i}`}
                            className={inputCls}
                            placeholder={`${i + 1}.`}
                            value={sp}
                            onChange={(e) => {
                              const next = [...form.selling_points];
                              next[i] = e.target.value;
                              setForm({ ...form, selling_points: next });
                            }}
                          />
                        ))}
                      </div>
                      <div className="mt-1">
                        <CopyButton
                          text={sellingPointsCopyText}
                          testId="copy-selling-points-button"
                        />
                      </div>
                    </Field>
                    <Field label={t("advertEn")}>
                      <textarea data-testid="property-advert-en-input" rows={5} className={inputCls} value={form.advert_en} onChange={set("advert_en")} />
                      <div className="mt-1"><CopyButton text={form.advert_en} testId="copy-advert-en-button" /></div>
                    </Field>
                    <Field label={t("advertFr")}>
                      <textarea data-testid="property-advert-fr-input" rows={5} className={inputCls} value={form.advert_fr} onChange={set("advert_fr")} />
                      <div className="mt-1"><CopyButton text={form.advert_fr} testId="copy-advert-fr-button" /></div>
                    </Field>
                  </div>
                </TabsContent>
                {form.id && (
                  <TabsContent value="offers">
                    <div className="pt-4">
                      <PropertyOffers propertyId={form.id} propertyName={form.name} />
                    </div>
                  </TabsContent>
                )}
                {form.id && (
                  <TabsContent value="viewings">
                    <div className="pt-4">
                      <PropertyViewings propertyId={form.id} propertyName={form.name} />
                    </div>
                  </TabsContent>
                )}
                {form.id && (
                  <TabsContent value="rooms">
                    <div className="pt-4">
                      <PropertyRooms propertyId={form.id} propertyName={form.name} />
                    </div>
                  </TabsContent>
                )}
                {form.id && (
                  <TabsContent value="parcels">
                    <div className="pt-4">
                      <PropertyParcels propertyId={form.id} propertyName={form.name} />
                    </div>
                  </TabsContent>
                )}
                {form.id && (
                  <TabsContent value="dossier">
                    <div className="pt-4">
                      <PropertyDossier propertyId={form.id} propertyName={form.name} />
                    </div>
                  </TabsContent>
                )}
                {form.id && (
                  <TabsContent value="timeline">
                    <div className="pt-4">
                      <Timeline linkKey="property_id" linkId={form.id} linkName={form.name} />
                    </div>
                  </TabsContent>
                )}
              </Tabs>
              <div className="flex items-center justify-between mt-6">
                {form.id ? (
                  <button data-testid="delete-property-button" onClick={remove} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#7F1D1D] hover:underline">
                    <Trash2 size={14} strokeWidth={1.5} /> {t("delete")}
                  </button>
                ) : <span />}
                <div className="flex gap-3">
                  <button data-testid="cancel-property-button" onClick={() => setForm(null)} className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-5 py-2 text-sm font-medium transition-colors">{t("cancel")}</button>
                  <button data-testid="save-property-button" onClick={save} disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2 text-sm font-medium transition-colors disabled:opacity-60">{t("save")}</button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {form && (
        <EmailShareDialog
          open={emailShareOpen}
          onClose={() => setEmailShareOpen(false)}
          contextName={form.name || ""}
          defaultSubject={`${form.ref_code ? form.ref_code + " · " : ""}${form.name || ""}`}
          files={[
            ...(form.documents || []).map((d) => ({ path: d.path, name: d.name || d.path.split("/").pop() })),
            ...(form.photos || []).map((p, i) => ({ path: p, name: `photo-${i + 1}.${p.split(".").pop()}` })),
            ...(form.floorplans || []).map((p, i) => ({ path: p, name: `floorplan-${i + 1}.${p.split(".").pop()}` })),
          ]}
        />
      )}
    </div>
  );
}

function PropertiesTable({ items, sortKey, sortDir, toggleSort, onOpen, t }) {
  const sorted = useMemo(() => {
    const list = [...items];
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (p) => {
      if (sortKey === "price_total") return p.price_total || 0;
      if (sortKey === "bedrooms") return p.bedrooms || 0;
      return (p[sortKey] || "").toString().toLowerCase();
    };
    list.sort((a, b) => (val(a) > val(b) ? dir : val(a) < val(b) ? -dir : 0));
    return list;
  }, [items, sortKey, sortDir]);
  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid="properties-table-wrap">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <SortableTh onClick={() => toggleSort("ref_code")} active={sortKey === "ref_code"} dir={sortDir}>{t("ref")}</SortableTh>
              <th className="py-2.5 px-3 w-14"></th>
              <SortableTh onClick={() => toggleSort("name")} active={sortKey === "name"} dir={sortDir}>{t("name")}</SortableTh>
              <SortableTh onClick={() => toggleSort("city")} active={sortKey === "city"} dir={sortDir}>{t("city")}</SortableTh>
              <SortableTh onClick={() => toggleSort("price_total")} active={sortKey === "price_total"} dir={sortDir} align="right">{t("priceTotal")}</SortableTh>
              <SortableTh onClick={() => toggleSort("bedrooms")} active={sortKey === "bedrooms"} dir={sortDir} align="right">{t("bedrooms")}</SortableTh>
              <SortableTh onClick={() => toggleSort("property_type")} active={sortKey === "property_type"} dir={sortDir}>{t("propertyType")}</SortableTh>
              <th className="py-2.5 px-3 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {sorted.map((p) => (
              <tr
                key={p.id}
                data-testid={`property-row-${p.id}`}
                onClick={() => onOpen(p)}
                className="hover:bg-stone-50 cursor-pointer transition-colors"
              >
                <td className="py-2 px-3"><RefBadge code={p.ref_code} /></td>
                <td className="py-2 px-3">
                  <img
                    src={p.photos?.length ? `${API}/files/${p.photos[0]}` : PLACEHOLDER}
                    alt=""
                    className="h-10 w-14 object-cover rounded border border-stone-200"
                    onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                  />
                </td>
                <td className="py-2 px-3 font-medium text-stone-900 max-w-[220px] truncate">{p.name}</td>
                <td className="py-2 px-3 text-stone-600 max-w-[160px] truncate">{p.city || p.location_public || "—"}</td>
                <td className="py-2 px-3 text-right tabular-nums whitespace-nowrap text-stone-900 font-medium">
                  {p.price_total != null ? fmtEuro(p.price_total) : "—"}
                </td>
                <td className="py-2 px-3 text-right text-stone-600">{p.bedrooms ?? "—"}</td>
                <td className="py-2 px-3 text-stone-600">{p.property_type || "—"}</td>
                <td className="py-2 px-3 text-right">
                  <Pencil size={14} strokeWidth={1.5} className="text-stone-300 inline-block" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
